/**
 * Resumable PHI upload modal powered by Uppy + tus.
 *
 * Why a separate component? Plain <input type="file"> + axios POST works for
 * < 50 MB but is not resumable. This modal fronts the /api/phi/tus/* endpoint
 * suite, which encrypts each PATCH body in-memory as a v2 chunked-AES-GCM
 * frame on the server side. Plaintext never persists to disk.
 *
 * Survives browser refresh: tus-js-client (Uppy's transport) drops a
 * `tus::*` localStorage entry per file, so an interrupted upload picks up
 * exactly where it left off.
 */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import Dashboard from '@uppy/react/dashboard';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { COLORS } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECORD_TYPES = ['lab', 'imaging', 'note', 'dicom', 'fhir', 'csv', 'other'];

export default function ResumableUploadModal({
  open,
  onClose,
  onUploaded,
  defaultRecordType = 'lab',
  targetUserId = '',                  // admin-on-behalf
  targetLabel = '',                   // shown as a confirmation banner
  maxFileSize = 500 * 1024 * 1024,    // 500 MB
}) {
  const [recordType, setRecordType] = useState(defaultRecordType);
  const [recordDateObj, setRecordDateObj] = useState(null);  // Date instance for shadcn Calendar
  const recordDate = recordDateObj ? format(recordDateObj, 'yyyy-MM-dd') : '';
  const [notes, setNotes] = useState('');
  const [uppy, setUppy] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const token = localStorage.getItem('auth_token');
    const instance = new Uppy({
      autoProceed: false,
      restrictions: { maxFileSize, allowedFileTypes: null },
      meta: { record_type: recordType, record_date: recordDate, notes, target_user_id: targetUserId },
    }).use(Tus, {
      endpoint: `${API}/phi/tus/uploads`,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      chunkSize: 5 * 1024 * 1024,
      removeFingerprintOnSuccess: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    instance.on('complete', (result) => {
      if (result.successful?.length) {
        if (onUploaded) onUploaded(result.successful);
      }
    });
    setUppy(instance);
    return () => {
      try { instance.destroy({ reason: 'unmount' }); } catch {}
      setUppy(null);
    };
  }, [open, recordType, recordDate, notes, targetUserId, maxFileSize, onUploaded]);

  if (!open) return null;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="resumable-upload-modal"
        style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 24, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: COLORS.textPrimary, fontSize: 20 }}>
            Resumable Upload (large or unstable connection)
          </h3>
          <button type="button" onClick={onClose} data-testid="resumable-close" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
          Files up to 500 MB. Drops are resumed automatically — even after a browser refresh. Plaintext never persists to disk;
          each chunk is encrypted (AES-256-GCM) on arrival.
        </p>

        {targetLabel && (
          <div data-testid="resumable-target-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', backgroundColor: `${COLORS.primary}10`, color: COLORS.primary, borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
            <span>Uploading on behalf of:</span> <span style={{ fontWeight: 700 }}>{targetLabel}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <select data-testid="resumable-record-type" value={recordType} onChange={(e) => setRecordType(e.target.value)}
            style={{ padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, backgroundColor: '#FFF' }}>
            {RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="resumable-record-date"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  fontSize: 14, backgroundColor: '#FFF', cursor: 'pointer', textAlign: 'left',
                  color: recordDateObj ? COLORS.textPrimary : COLORS.textSecondary,
                  fontFamily: 'inherit',
                }}>
                <span>{recordDateObj ? format(recordDateObj, 'PPP') : 'Pick a date (optional)'}</span>
                <CalendarIcon size={16} color={COLORS.textSecondary} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={recordDateObj}
                onSelect={setRecordDateObj}
                disabled={(d) => d > new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <textarea data-testid="resumable-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, marginBottom: 12, fontFamily: 'inherit', resize: 'vertical' }} />

        {uppy && (
          <Dashboard
            uppy={uppy}
            proudlyDisplayPoweredByUppy={false}
            height={360}
            width="100%"
            theme="light"
            note="Drag a single PHI file (PDF, image, DICOM, FHIR JSON, CSV)."
          />
        )}
      </div>
    </div>
  );
}
